import React from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import uiConfig from "../../config/uiConfig";
import "./FocusPartsCharts.css";

/**
 * Props:
 *   chartType - "quantity" | "potentialValue" | "uio" | "revenue" | "penetration" | "totalQty" | "all" | "line" | "bar" | "pie" | "area"
 *   data - chart data object from AI stream response (can be old format or new graphData format)
 *   compact - boolean, if true render smaller charts for chat display
 */
const FocusPartsCharts = ({ chartType = "all", data = {}, compact = false }) => {
  const chartData = data || {};
  const chartColors = uiConfig.chartPalette || ["#bb1ccc", "#5e0768", "#0ea5e9"];

  // Format Y-axis labels with K (thousands), M (millions), and Bn (billions)
  const formatYAxisLabel = (value) => {
    if (value >= 1000000000) {
      return (value / 1000000000).toFixed(1).replace(/\.0$/, "") + "Bn";
    }
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
    }
    if (value >= 1000) {
      return (value / 1000).toFixed(1).replace(/\.0$/, "") + "K";
    }
    return value;
  };

  // Check if this is the new graphData format (has type, data, x_key, y_keys fields)
  const isGraphDataFormat = chartData.type && chartData.data && chartData.x_key && chartData.y_keys;
  
  if (isGraphDataFormat) {
    // Handle new graphData format
    const { type, data: chartArray, x_key, y_keys, title } = chartData;
    
    if (!chartArray || chartArray.length === 0) {
      return (
        <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
          No chart data available
        </div>
      );
    }

    const chartHeight = compact ? 420 : 500;
    
    // Handle different chart types
    if (type === "pie") {
      // Pie chart - different structure
      const dataKey = y_keys[0] || "qty";
      return (
        <div style={{ width: "100%", height: "100%", minHeight: 300, marginBottom: "12px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Tooltip formatter={(value) => value.toLocaleString ? value.toLocaleString() : value} />
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                height={30}
                wrapperStyle={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', paddingTop: '3px', gap: '8px' }}
              />
              <Pie
                data={chartArray}
                dataKey={dataKey}
                nameKey={x_key}
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {chartArray.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={chartColors[idx % chartColors.length]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      );
    }

    // For line, bar, area - use cartesian coordinates
    const renderGraphChart = (ChartComponent) => (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ChartComponent data={chartArray} margin={{ top: 50, right: 10, left: 10, bottom: 20 }}>
          <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={uiConfig.chartPrimaryColor} />
              <stop offset="100%" stopColor={uiConfig.aiAssistantSecondaryColor} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#f0f0f0" />
          <XAxis
            dataKey={x_key}
          />
          <YAxis tickFormatter={formatYAxisLabel} />
          <Tooltip
            formatter={(value) => (typeof value === "number" ? value.toLocaleString() : value)}
            labelFormatter={(label) => `${x_key}: ${label}`}
          />
          <Legend 
            layout="horizontal" 
            verticalAlign="bottom" 
            height={10}
            wrapperStyle={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', paddingTop: '3px', gap: '8px' }}
          />
          {y_keys.map((key, idx) => {
            if (type === "area") {
              return (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  fill={chartColors[idx % chartColors.length]}
                  stroke={chartColors[idx % chartColors.length]}
                  name={key}
                  fillOpacity={0.6}
                />
              );
            } else if (type === "bar") {
              return (
                <Bar
                  radius={[8, 8, 0, 0]}
                  key={key}
                  dataKey={key}
                  fill="url(#barGradient)"
                  name={key}
                />
              );
            } else {
              // line or default
              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={chartColors[idx % chartColors.length]}
                  strokeWidth={2}
                  dot={{ r: 5 }}
                  activeDot={{ r: 7 }}
                  name={key}
                />
              );
            }
          })}
        </ChartComponent>
      </ResponsiveContainer>
    );

    const componentMap = {
      bar: BarChart,
      line: LineChart,
      area: AreaChart,
      pie: null, // Handled separately above
    };
    
    const ChartComponent = componentMap[type] || LineChart;

    return (
      <div style={{ width: "100%", height: "100%", minHeight: chartHeight, marginBottom: "12px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        {renderGraphChart(ChartComponent)}
      </div>
    );
  }

  // Handle old chartData format
  if (!chartData || Object.keys(chartData).length === 0) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
        No chart data available
      </div>
    );
  }

const chartHeight = compact ? 420 : 500;
  const containerClass = compact ? "charts-compact" : "focus-parts-charts-container";

  const renderChart = (title, ChartComponent, props) => (
    <div className="chart-section" key={title}>
      <h2>{title}</h2>
      <div style={{ flex: 1, display: "flex", minHeight: 0, width: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <ChartComponent {...props} />
        </ResponsiveContainer>
      </div>
    </div>
  );

  return (
    <div className={containerClass}>
      {!compact && <h1 className="charts-title">Focus Parts Benchmarking - Visual Analytics</h1>}

      {chartType === "quantity" &&
        chartData.quantityByCountry?.length > 0 &&
        renderChart(
          "Quantity per 100 UIO by Country",
          BarChart,
          {
            data: chartData.quantityByCountry,
            children: [
              <CartesianGrid stroke="#f0f0f0" key="grid" />,
              <XAxis
                dataKey="country"
                angle={-45}
                textAnchor="end"
                height={80}
                key="xaxis"
              />,
              <YAxis key="yaxis" tickFormatter={formatYAxisLabel} />,
              <Tooltip
                formatter={(value) => value.toFixed(2)}
                key="tooltip"
              />,
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                height={30}
                wrapperStyle={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', paddingTop: '3px', gap: '8px' }}
                key="legend" 
              />,
              <ReferenceLine
                y={23.47}
                stroke={chartColors[6] || "#999"}
                label="Benchmark: 23.47"
                key="refline"
              />,
              <Bar
                  radius={[8, 8, 0, 0]} dataKey="qty" fill="url(#barGradient)" name="Qty/100UIO" key="bar" />,
            ],
          }
        )}

      {chartType === "potentialValue" &&
        chartData.topParts?.length > 0 &&
        renderChart(
          "Potential Value by Part (Top 10)",
          BarChart,
          {
            layout: "vertical",
            data: chartData.topParts,
            children: [
              <defs key="defs">
                <linearGradient id="barGradient2" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={uiConfig.chartPrimaryColor} />
                  <stop offset="100%" stopColor={uiConfig.aiAssistantSecondaryColor} />
                </linearGradient>
              </defs>,
              <CartesianGrid stroke="#f0f0f0" key="grid" />,
              <XAxis type="number" key="xaxis" />,
              <YAxis
                dataKey="part"
                type="category"
                width={80}
                key="yaxis"
              />,
              <Tooltip
                formatter={(value) => `$${value.toLocaleString()}`}
                key="tooltip"
              />,
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                height={30}
                wrapperStyle={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', paddingTop: '3px', gap: '8px' }}
                key="legend" 
              />,
              <Bar
                  radius={[8, 8, 0, 0]}
                dataKey="value"
                fill="url(#barGradient2)"
                name="Potential Value ($)"
                key="bar"                radius={[0, 8, 8, 0]}              />,
            ],
          }
        )}

      {chartType === "uio" &&
        chartData.uioByYear?.length > 0 &&
        renderChart(
          "Units in Operation (UIO) by Year",
          LineChart,
          {
            data: chartData.uioByYear,
            children: [
              <CartesianGrid stroke="#f0f0f0" key="grid" />,
              <XAxis dataKey="year" key="xaxis" />,
              <YAxis key="yaxis" tickFormatter={formatYAxisLabel} />,
              <Tooltip
                formatter={(value) => value.toLocaleString()}
                key="tooltip"
              />,
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                height={30}
                wrapperStyle={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', paddingTop: '3px', gap: '8px' }}
                key="legend" 
              />,
              <Line
                type="monotone"
                dataKey="uio"
                stroke={chartColors[2]}
                strokeWidth={2}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
                name="UIO"
                key="line"
              />,
            ],
          }
        )}

      {chartType === "revenue" &&
        chartData.revenueByMonth?.length > 0 &&
        renderChart(
          "Total Net Revenue (NR) by Month",
          LineChart,
          {
            data: chartData.revenueByMonth,
            children: [
              <CartesianGrid stroke="#f0f0f0" key="grid" />,
              <XAxis dataKey="month" key="xaxis" />,
              <YAxis key="yaxis" tickFormatter={formatYAxisLabel} />,
              <Tooltip
                formatter={(value) => `$${value.toLocaleString()}`}
                key="tooltip"
              />,
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                height={30}
                wrapperStyle={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', paddingTop: '3px', gap: '8px' }}
                key="legend" 
              />,
              <Line
                type="monotone"
                dataKey="revenue"
                stroke={chartColors[3]}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                name="Revenue ($)"
                key="line"
              />,
            ],
          }
        )}

      {chartType === "penetration" &&
        chartData.penetrationByCountry?.length > 0 &&
        renderChart(
          "Parts Penetration % by Country",
          BarChart,
          {
            data: chartData.penetrationByCountry,
            children: [
              <defs key="defs">
                <linearGradient id="barGradient3" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={uiConfig.chartPrimaryColor} />
                  <stop offset="100%" stopColor={uiConfig.aiAssistantSecondaryColor} />
                </linearGradient>
              </defs>,
              <CartesianGrid stroke="#f0f0f0" key="grid" />,
              <XAxis
                dataKey="country"
                angle={-45}
                textAnchor="end"
                height={80}
                key="xaxis"
              />,
              <YAxis key="yaxis" tickFormatter={formatYAxisLabel} />,
              <Tooltip formatter={(value) => `${value}%`} key="tooltip" />,
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                height={30}
                wrapperStyle={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', paddingTop: '3px', gap: '8px' }}
                key="legend" 
              />,
              <Bar
                  radius={[8, 8, 0, 0]}
                dataKey="penetration"
                fill="url(#barGradient3)"
                name="Penetration (%)"
                key="bar"                radius={[8, 8, 0, 0]}              />,
            ],
          }
        )}

      {chartType === "totalQty" &&
        chartData.qtyByYear?.length > 0 &&
        renderChart(
          "Total Quantity by Year",
          LineChart,
          {
            data: chartData.qtyByYear,
            children: [
              <CartesianGrid stroke="#f0f0f0" key="grid" />,
              <XAxis dataKey="year" key="xaxis" />,
              <YAxis key="yaxis" tickFormatter={formatYAxisLabel} />,
              <Tooltip
                formatter={(value) => value.toLocaleString()}
                key="tooltip"
              />,
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                height={30}
                wrapperStyle={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', paddingTop: '3px', gap: '8px' }}
                key="legend" 
              />,
              <Line
                type="monotone"
                dataKey="qty"
                stroke={chartColors[5]}
                strokeWidth={2}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
                name="Total Qty"
                key="line"
              />,
            ],
          }
        )}

      {chartType === "all" &&
        chartData.quantityByCountry?.length > 0 &&
        renderChart(
          "Quantity per 100 UIO by Country",
          BarChart,
          {
            data: chartData.quantityByCountry,
            children: [
              <defs key="defs">
                <linearGradient id="barGradient4" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={uiConfig.chartPrimaryColor} />
                  <stop offset="100%" stopColor={uiConfig.aiAssistantSecondaryColor} />
                </linearGradient>
              </defs>,
              <CartesianGrid stroke="#f0f0f0" key="grid" />,
              <XAxis
                dataKey="country"
                angle={-45}
                textAnchor="end"
                height={80}
                key="xaxis"
              />,
              <YAxis key="yaxis" tickFormatter={formatYAxisLabel} />,
              <Tooltip
                formatter={(value) => value.toFixed(2)}
                key="tooltip"
              />,
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                height={30}
                wrapperStyle={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', paddingTop: '3px', gap: '8px' }}
                key="legend" 
              />,
              <ReferenceLine
                y={23.47}
                stroke={chartColors[6] || "#999"}
                label="Benchmark: 23.47"
                key="refline"
              />,
              <Bar
                  radius={[8, 8, 0, 0]} dataKey="qty" fill="url(#barGradient)" name="Qty/100UIO" key="bar" />,
            ],
          }
        )}

      {Object.values(chartData).every(
        (v) =>
          Array.isArray(v) && v.length === 0
      ) && (
        <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
          No chart data available
        </div>
      )}
    </div>
  );
};

export default FocusPartsCharts;
